/* assets/timeline.js */
(function () {
  'use strict';

  const SECTION_SELECTOR = '.vertical-timeline-wrapper';
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const stepVH = () => window.innerHeight * 0.5; // 50% per panel

  /* ---------------- Viewport stabilizer ---------------- */
  function getStabilizeTarget(wrapper) {
    // Measure the sticky element to match the visual pinning
    return wrapper.querySelector('.timeline-background') || wrapper;
  }
  function stabilizeAround(wrapper, mutateFn) {
    const target = getStabilizeTarget(wrapper);
    const beforeTop = target.getBoundingClientRect().top;
    mutateFn();
    requestAnimationFrame(() => {
      const afterTop = target.getBoundingClientRect().top;
      window.scrollBy(0, afterTop - beforeTop);
    });
  }

  /* ---------------- Slider ---------------- */
  function setupSlider(sliderEl, sectionState) {
    if (!sliderEl || sliderEl.__initialized) return;

    const viewport = sliderEl.querySelector('[data-viewport]');
    const grid     = sliderEl.querySelector('[data-grid]');
    const items    = Array.from(grid ? grid.children : []);
    const prevBtn  = sliderEl.querySelector('[data-prev]');
    const nextBtn  = sliderEl.querySelector('[data-next]');
    const counter  = sliderEl.querySelector('[data-counter]');
    const controls = sliderEl.querySelector('[data-controls]');
    const perPage  = 6;

    // Containment
    sliderEl.style.overflowX ||= 'clip';
    if (viewport) {
      viewport.style.overflow = 'hidden';
      viewport.style.width = '100%';
      viewport.style.position = 'relative';
      viewport.style.touchAction = 'pan-y pinch-zoom'; // vertical scroll remains natural
    }

    if (!grid || !items.length) {
      if (controls) controls.style.display = 'none';
      sliderEl.__initialized = true;
      sliderEl.__update = () => {};
      return;
    }

    // Build track/slides
    const track = document.createElement('div');
    track.className = 'slider-track';
    track.style.display = 'flex';
    track.style.willChange = 'transform';
    viewport.appendChild(track);

    for (let i = 0; i < items.length; i += perPage) {
      const slide = document.createElement('div');
      slide.className = 'slide';
      slide.style.flex = '0 0 100%';
      slide.style.minWidth = '100%';
      const slideGrid = document.createElement('div');
      slideGrid.className = 'timeline-grid';
      slideGrid.style.minWidth = '0';
      items.slice(i, i + perPage).forEach(n => {
        if (n.style) n.style.minWidth = '0';
        slideGrid.appendChild(n);
      });
      slide.appendChild(slideGrid);
      track.appendChild(slide);
    }
    grid.remove();

    const slides = Array.from(track.children);
    let index = 0;

    function update() {
      track.style.transform = `translateX(-${index * 100}%)`;
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      const atStart = index === 0;
      const atEnd   = index === slides.length - 1;
      if (prevBtn) { prevBtn.disabled = atStart; prevBtn.setAttribute('aria-disabled', String(atStart)); }
      if (nextBtn) { nextBtn.disabled = atEnd;   nextBtn.setAttribute('aria-disabled', String(atEnd)); }
      if (controls) controls.style.display = slides.length > 1 ? 'flex' : 'none';
    }

    function tryPrev() { if (index > 0) { index--; update(); } }
    function tryNext() { if (index < slides.length - 1) { index++; update(); } }

    // --- lock helpers: suspend scrollytelling while user uses slider ---
    let busyTimer = null;
    const setBusy = (busy) => {
      clearTimeout(busyTimer);
      if (busy) {
        sectionState.sliderBusy = true;
      } else {
        // small grace to avoid flicker on tap end
        busyTimer = setTimeout(() => { sectionState.sliderBusy = false; }, 160);
      }
    };

    // Buttons
    prevBtn && prevBtn.addEventListener('click', (e) => { if (!prevBtn.disabled) { setBusy(true); tryPrev(); setBusy(false); } e.stopPropagation(); });
    nextBtn && nextBtn.addEventListener('click', (e) => { if (!nextBtn.disabled) { setBusy(true); tryNext(); setBusy(false); } e.stopPropagation(); });

    // Drag/Swipe (clamped)
    let startX = 0, currX = 0, dragging = false, mouseDown = false;
    const viewW = () => viewport.clientWidth;

    const begin = x => { setBusy(true); dragging = true; startX = x; track.style.transition = 'none'; };
    const move  = x => {
      if (!dragging) return;
      currX = x;
      const dx = clamp(currX - startX, -viewW(), viewW());
      track.style.transform = `translateX(calc(-${index * 100}% + ${dx}px))`;
    };
    const end   = () => {
      if (!dragging) return;
      dragging = false;
      const dx = (currX || startX) - startX;
      track.style.transition = 'transform .4s ease';
      const threshold = Math.min(60, viewW() * 0.14);
      if (dx < -threshold) tryNext();
      else if (dx > threshold) tryPrev();
      else update(); // snap back
      currX = 0;
      setBusy(false);
    };

    viewport.addEventListener('touchstart', e => { begin(e.touches[0].clientX); }, { passive: true });
    viewport.addEventListener('touchmove',  e => move(e.touches[0].clientX),     { passive: true });
    viewport.addEventListener('touchend',   end,                                 { passive: true });
    viewport.addEventListener('touchcancel',end,                                 { passive: true });

    viewport.addEventListener('mousedown', e => { mouseDown = true; begin(e.clientX); e.preventDefault(); });
    viewport.addEventListener('mousemove', e => { if (!mouseDown) return; move(e.clientX); });
    ['mouseup','mouseleave'].forEach(ev => viewport.addEventListener(ev, () => { if (!mouseDown) return; mouseDown = false; end(); }));

    // Keyboard arrows on viewport
    viewport.tabIndex = viewport.tabIndex || 0;
    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); setBusy(true); tryNext(); setBusy(false); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setBusy(true); tryPrev(); setBusy(false); }
      e.stopPropagation();
    });

    window.addEventListener('resize', () => update());
    sliderEl.__initialized = true;
    sliderEl.__update = update;
    update();
  }

  /* ---------------- Tabs / Panels ---------------- */
  function setActive(index, ctx, opts = {}) {
    const { tabs, panels } = ctx;
    index = clamp(index, 0, panels.length - 1);

    tabs.forEach((t, i) => {
      const sel = i === index;
      t.setAttribute('aria-selected', sel ? 'true' : 'false');
      t.tabIndex = sel ? 0 : -1;
      t.classList.toggle('active', sel);
    });

    panels.forEach((p, i) => {
      const active = i === index;
      p.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        p.removeAttribute('hidden');
        p.classList.add('is-active'); // CSS handles fade
        const slider = p.querySelector('[data-slider]');
        if (slider && !slider.__initialized) setupSlider(slider, ctx.__state);
        else if (slider && slider.__update) slider.__update();
      } else {
        p.classList.remove('is-active');
        p.setAttribute('hidden','');
      }
    });

    if (opts.focusTab) tabs[index].focus();
    ctx.__state.activeIndex = index;
  }

  /* ---------------- Engage/hover gate ---------------- */
  function makeHoverGate(inner, wrapper, onChange) {
    let drive = false;
    let leaveTimer = null;

    const rectContains = (el, x, y) => {
      const r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };
    const setDrive = (val) => {
      if (drive === val) return;
      drive = val;
      onChange && onChange(drive);
    };

    // Pointer tracking with debounced exit
    document.addEventListener('pointermove', (e) => {
      const inside = rectContains(inner, e.clientX, e.clientY);
      if (inside) { clearTimeout(leaveTimer); setDrive(true); }
      else { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); }
    }, { passive: true });

    inner.addEventListener('pointerenter', () => { clearTimeout(leaveTimer); setDrive(true); });
    inner.addEventListener('pointerleave', () => { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); });

    // Wheel inside/outside
    wrapper.addEventListener('wheel', (e) => {
      if (inner.contains(e.target)) { clearTimeout(leaveTimer); setDrive(true); }
      else { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); }
    }, { passive: true });

    // Focus a11y
    inner.addEventListener('focusin', () => { clearTimeout(leaveTimer); setDrive(true); });
    inner.addEventListener('focusout', (e) => {
      if (!inner.contains(e.relatedTarget)) { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); }
    });

    // Touch
    inner.addEventListener('touchstart', () => { clearTimeout(leaveTimer); setDrive(true); }, { passive: true });
    document.addEventListener('touchend',   () => { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); }, { passive: true });
    document.addEventListener('touchcancel',() => { clearTimeout(leaveTimer); leaveTimer = setTimeout(() => setDrive(false), 120); }, { passive: true });

    return () => drive;
  }

  /* ---------------- Scroll mapping ---------------- */
  function computeActiveIndex(wrapper, count) {
    const rect = wrapper.getBoundingClientRect();
    const wrapperTopAbs = window.scrollY + rect.top;
    const maxDist = Math.max(0, wrapper.offsetHeight - window.innerHeight);
    const dist = clamp(window.scrollY - wrapperTopAbs, 0, maxDist);
    return clamp(Math.floor(dist / stepVH()), 0, count - 1);
  }

  function bindTabs(wrapper, inner, background, ctx) {
    const { tabs } = ctx;

    function goTo(i, focusTab = true) {
      stabilizeAround(wrapper, () => {
        inner.classList.add('is-pinned');
        wrapper.classList.add('is-engaged');
        if (background) background.classList.add('is-engaged');
      });
      const top = window.scrollY + wrapper.getBoundingClientRect().top + i * stepVH();
      window.scrollTo({ top, behavior: 'smooth' });
      setActive(i, ctx, { focusTab });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', (e) => { e.stopPropagation(); goTo(i, true); });
      tab.addEventListener('keydown', (e) => {
        const keys = ['ArrowRight','ArrowDown','ArrowLeft','ArrowUp','Home','End','Enter',' '];
        if (!keys.includes(e.key)) return;
        e.preventDefault();
        const count = ctx.panels.length;
        let next = i;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % count;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + count) % count;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = count - 1;
        goTo(next, true);
      });
    });
  }

  /* ---------------- Init per section ---------------- */
  function initTimelineSection(wrapper) {
    if (!wrapper || wrapper.__timelineInit) return;
    wrapper.__timelineInit = true;

    const inner      = wrapper.querySelector('.vertical-timeline-inner');
    const background = wrapper.querySelector('.timeline-background');
    const tabs       = Array.from(wrapper.querySelectorAll('[role="tab"]'));
    const panels     = Array.from(wrapper.querySelectorAll('[role="tabpanel"]'));
    if (!inner || !tabs.length || !panels.length) return;

    const state = { activeIndex: 0, sliderBusy: false, engaged: false };
    const ctx = { tabs, panels, __state: state };

    // Engage/disengage gate with stabilization — but DO NOT change active panel on engage
    const gate = makeHoverGate(inner, wrapper, (isInside) => {
      if (state.sliderBusy) return; // slider interaction should not flip engage
      if (state.engaged === isInside) return; // no change -> no work
      state.engaged = isInside;

      stabilizeAround(wrapper, () => {
        inner.classList.toggle('is-pinned',    isInside);
        wrapper.classList.toggle('is-engaged', isInside);
        if (background) background.classList.toggle('is-engaged', isInside);
      });
      // Do NOT call setActive() here — preserve current panel
    });

    // Initial engage sync (in case pointer/focus already inside)
    const initiallyEngaged = gate();
    state.engaged = initiallyEngaged;
    stabilizeAround(wrapper, () => {
      inner.classList.toggle('is-pinned',    initiallyEngaged);
      wrapper.classList.toggle('is-engaged', initiallyEngaged);
      if (background) background.classList.toggle('is-engaged', initiallyEngaged);
    });

    setActive(0, ctx);

    bindTabs(wrapper, inner, background, ctx);

    // Scroll/resize → panel activation (only when engaged and not slider-busy)
    let ticking = false;
    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (!state.engaged || state.sliderBusy) { ticking = false; return; }
        const idx = computeActiveIndex(wrapper, panels.length);
        if (idx !== state.activeIndex) setActive(idx, ctx);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    setTimeout(onScrollOrResize, 80);

    // Theme editor cleanup
    wrapper.__timelineDestroy = () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      wrapper.__timelineInit = false;
    };
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initTimelineSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const sec = e.target?.querySelector(SECTION_SELECTOR);
    if (sec) initTimelineSection(sec);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sec = e.target?.querySelector(SECTION_SELECTOR);
    if (sec && sec.__timelineDestroy) sec.__timelineDestroy();
  });
})();
